import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { BehaviorSubject, firstValueFrom } from 'rxjs'
import { Feature, FeatureCollection } from 'geojson'
import * as h3 from 'h3-js'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import {
  isAreaSimWarningDataModel,
  isSegmentSimWarningDataModel,
  SimWarningDataModel,
  SimWarningValidationResult,
} from 'src/app/shared/models/sim-warning-creation.model'

@Injectable()
export class SimulationWarningsEditorDataService {
  public readonly validWarnings$ = new BehaviorSubject<
    Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>[]
  >([])
  public readonly corruptedWarnings$ = new BehaviorSubject<
    Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>[]
  >([])
  // Temporary compatibility shim for the deprecated legacy import format.
  // Remove once all consumers have moved to the GeoJSON-based format.
  private readonly legacyImportCompatibilityEnabled = true

  constructor(
    private readonly http: HttpClient,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async importFromFeatureCollection(
    featureCollection: FeatureCollection<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
  ): Promise<void> {
    const tempWarnings = featureCollection.features.map((feature) => {
      this.normalizeWarningFeature(feature)
      return feature
    })

    const ids = tempWarnings.map((feature) => {
      const warning = feature.properties
      return isAreaSimWarningDataModel(warning) ? warning.h3id : warning.segmentId
    })
    const validationResults = await this.validate(ids)

    const validIds = new Set(validationResults.filter((r) => r.isValid).map((r) => r.id))
    const isValidWarning = (
      feature: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
    ): boolean => {
      const warning = feature.properties
      let warningId: string | undefined
      if (isAreaSimWarningDataModel(warning)) {
        warningId = warning.h3id
      } else if (isSegmentSimWarningDataModel(warning)) {
        warningId = warning.segmentId
      }

      const isValid = warningId !== undefined && validIds.has(warningId)
      warning.valid = isValid
      return isValid
    }

    const validWarnings = tempWarnings.filter(isValidWarning)
    const corruptedWarnings = tempWarnings.filter((feature) => !isValidWarning(feature))

    this.validWarnings$.next(validWarnings)
    this.corruptedWarnings$.next(corruptedWarnings)
  }

  async importLegacyWarnings(raw: unknown): Promise<void> {
    if (!this.legacyImportCompatibilityEnabled) return

    const featureCollection = this.normalizeLegacyWarnings(raw)
    await this.importFromFeatureCollection(featureCollection)
  }

  private normalizeLegacyWarnings(
    raw: unknown,
  ): FeatureCollection<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel> {
    const entries = Array.isArray(raw) ? raw : [raw]
    const features = entries
      .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === 'object')
      .map((entry) => this.createFeatureFromLegacyWarning(entry))

    return {
      type: 'FeatureCollection',
      features,
    }
  }

  private createFeatureFromLegacyWarning(
    warning: Record<string, unknown>,
  ): Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel> {
    const source = { ...(warning ?? {}) }
    const featureId =
      typeof source['featureId'] === 'string' ? source['featureId'] : crypto.randomUUID()
    const ttlValue =
      typeof source['ttl'] === 'string' || typeof source['ttl'] === 'number'
        ? Number(source['ttl']) || 0
        : 0
    const targetsValue =
      source['targets'] === null || source['targets'] === undefined
        ? null
        : Array.isArray(source['targets'])
          ? source['targets'].map((target) => String(target))
          : [String(source['targets'])]

    const properties = {
      ...source,
      featureId,
      ttl: ttlValue,
      targets: targetsValue,
      confidence: typeof source['confidence'] === 'number' ? source['confidence'] : 1.0,
      dfrsClassification:
        (source['dfrsClassification'] as SimWarningDataModel['dfrsClassification']) ?? 'DFRS_NONE',
      valid: typeof source['valid'] === 'boolean' ? source['valid'] : true,
    } as SimWarningDataModel

    return {
      type: 'Feature',
      geometry: this.buildLegacyGeometry(properties),
      properties,
    }
  }

  private buildLegacyGeometry(warning: SimWarningDataModel): GeoJSON.LineString | GeoJSON.Polygon {
    if ('geometry' in warning && warning.geometry && typeof warning.geometry === 'object') {
      const geometry = warning.geometry as GeoJSON.Geometry
      if (geometry.type === 'LineString' || geometry.type === 'Polygon') {
        return geometry
      }
      if (geometry.type === 'Point') {
        const [longitude, latitude] = geometry.coordinates as [number, number]
        return {
          type: 'LineString',
          coordinates: [
            [longitude, latitude],
            [longitude + 0.00005, latitude + 0.00005],
          ],
        }
      }
    }

    const longitude = Number((warning as any)?.longitude)
    const latitude = Number((warning as any)?.latitude)

    if (isAreaSimWarningDataModel(warning) && warning.h3id) {
      const boundary = h3.cellToBoundary(warning.h3id, true)
      return {
        type: 'Polygon',
        coordinates: [boundary.map(([lat, lng]) => [lng, lat])],
      }
    }

    if (
      isSegmentSimWarningDataModel(warning) &&
      Number.isFinite(longitude) &&
      Number.isFinite(latitude)
    ) {
      const offset = 0.00005
      return {
        type: 'LineString',
        coordinates: [
          [longitude, latitude],
          [longitude + offset, latitude + offset],
        ],
      }
    }

    if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
      return {
        type: 'LineString',
        coordinates: [
          [longitude, latitude],
          [longitude + 0.00005, latitude + 0.00005],
        ],
      }
    }

    return {
      type: 'LineString',
      coordinates: [
        [0, 0],
        [0.00005, 0.00005],
      ],
    }
  }

  private normalizeWarningFeature(
    feature: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
  ): void {
    const properties = feature.properties ?? {}

    if (!properties.featureId) {
      properties.featureId = crypto.randomUUID()
    }
  }

  async validate(ids: string[]): Promise<SimWarningValidationResult[]> {
    const httpOptions = {
      headers: new HttpHeaders({ accept: 'json' }),
    }
    const url = `${this.config.backend_url}rcs/validate-sim-warnings`
    return firstValueFrom(this.http.post<SimWarningValidationResult[]>(url, ids, httpOptions))
  }

  addWarning(feature: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>): void {
    this.normalizeWarningFeature(feature)
    this.validWarnings$.next([...this.validWarnings$.value, feature])
  }

  getValidWarnings(): Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>[] {
    return this.validWarnings$.value
  }

  getCorruptedWarnings(): Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>[] {
    return this.corruptedWarnings$.value
  }

  exportToFeatureCollection(): FeatureCollection<
    GeoJSON.LineString | GeoJSON.Polygon,
    SimWarningDataModel
  > {
    return {
      type: 'FeatureCollection',
      features: this.validWarnings$.value.map((feature) => this.stripFeatureId(feature)),
    }
  }

  private stripFeatureId(
    feature: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>,
  ): Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel> {
    const normalizedFeature = {
      ...feature,
      properties: {
        ...feature.properties,
      },
    }

    delete normalizedFeature.properties.featureId
    return normalizedFeature
  }

  clearWarnings(): void {
    this.validWarnings$.next([])
    this.corruptedWarnings$.next([])
  }

  updateWarning(feature: Feature<GeoJSON.LineString | GeoJSON.Polygon, SimWarningDataModel>): void {
    const updated = this.validWarnings$.value.map((w) =>
      w.properties.featureId === feature.properties.featureId ? feature : w,
    )
    this.validWarnings$.next(updated)
  }

  removeWarning(featureId: string): void {
    this.validWarnings$.next(
      this.validWarnings$.value.filter((w) => w.properties.featureId !== featureId),
    )
    this.corruptedWarnings$.next(
      this.corruptedWarnings$.value.filter((w) => w.properties.featureId !== featureId),
    )
  }

  async pushAllToSimulation(): Promise<{ succeeded: number; failed: number }> {
    const httpOptions = {
      headers: new HttpHeaders({ accept: 'json' }),
      observe: 'response' as const,
    }

    const payloads = this.validWarnings$.value.map(
      (warning) => this.stripFeatureId(warning).properties,
    )
    const areaPayloads = payloads.filter((payload) => isAreaSimWarningDataModel(payload))
    const segmentPayloads = payloads.filter((payload) => isSegmentSimWarningDataModel(payload))

    let succeeded = 0
    let failed = 0

    const pushGroup = async (
      groupPayloads: Partial<SimWarningDataModel>[],
      endpoint: string,
    ): Promise<void> => {
      if (!groupPayloads.length) return
      try {
        const response = await firstValueFrom(
          this.http.put(`${this.config.backend_url}rcs/${endpoint}`, groupPayloads, httpOptions),
        )
        if (response.status >= 200 && response.status < 300) {
          succeeded += groupPayloads.length
        } else {
          failed += groupPayloads.length
        }
      } catch {
        failed += groupPayloads.length
      }
    }

    await pushGroup(areaPayloads, 'area-warnings')
    await pushGroup(segmentPayloads, 'roadsegment-warnings')

    return { succeeded, failed }
  }
}
